/**
 * sl-9qm.19: InternalViewOverlay — Workspace-Internal Dashboard View
 *
 * Renders an AppBase in mode='internal' with Y.js live data.
 * Displayed as an overlay in the editor (same pattern as PublishEditor).
 *
 * SOLID:
 * - S: Only renders the internal view overlay (no config editing)
 * - D: Depends on ViewConfigStore, ViewRenderer, ComponentRegistry
 */
import { useLiveData, useService } from '@toeverything/infra';
import type { FC } from 'react';

import { ViewConfigStore } from '../../stores/view-config.store';
import { ViewRenderer } from '../view-renderer/view-renderer';
import * as styles from './styles.css';

export interface InternalViewOverlayProps {
  onClose: () => void;
}

/**
 * Overlay that renders a ViewConfig in internal mode.
 * Uses Y.js live data via YjsViewDataSource (selected automatically by ViewDataSourceFactory).
 */
export const InternalViewOverlay: FC<InternalViewOverlayProps> = ({
  onClose,
}) => {
  const viewConfigStore = useService(ViewConfigStore);
  const config = useLiveData(viewConfigStore.config$);

  const title = config?.settings?.title || 'Internal View';

  return (
    <div className={styles.internalViewOverlay}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <span className={styles.toolbarTitle}>{title}</span>
        <div className={styles.toolbarSpacer} />
        <button className={styles.toolbarButton} onClick={onClose}>
          Close
        </button>
      </div>

      {/* View Content */}
      <div className={styles.viewContainer}>
        {config ? (
          <ViewRenderer config={config} mode="internal" />
        ) : (
          <div className={styles.emptyState}>
            <span>No view configuration found.</span>
            <span>Open the Publish Editor to create a view first.</span>
          </div>
        )}
      </div>
    </div>
  );
};
